// Utilitários para formatação de preços

/**
 * Formata um valor numérico para o formato de moeda brasileira
 * @param value - Valor numérico
 * @returns String formatada como "R$ 0,00"
 */
export const formatPrice = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numericValue)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numericValue);
};

/**
 * Remove formatação de preço e retorna apenas o valor numérico
 * @param formattedValue - Valor formatado como "R$ 1.234,56"
 * @returns Número decimal
 */
export const parsePrice = (formattedValue: string): number => {
  if (!formattedValue) return 0;
  
  // Remove "R$", espaços e substitui vírgula por ponto
  const cleanValue = formattedValue
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  
  const numericValue = parseFloat(cleanValue);
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Remove formatação de preço simples e retorna apenas o valor numérico
 * @param formattedValue - Valor formatado como "R$ 1.234,56"
 * @returns Número decimal
 */
export const parseSimplePrice = (formattedValue: string): number => {
  if (!formattedValue) return 0;
  
  // Remove "R$" e espaços
  let cleanValue = formattedValue.replace(/[R$\s]/g, '');
  
  // Se tem vírgula, trata como separador decimal
  if (cleanValue.includes(',')) {
    // Remove pontos (milhares) e substitui vírgula por ponto
    cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
  }
  
  const numericValue = parseFloat(cleanValue);
  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Aplica máscara de preço em tempo real durante a digitação
 * @param value - Valor sendo digitado
 * @returns Valor formatado com máscara
 */
export const applyPriceMask = (value: string): string => {
  // Remove tudo que não é dígito
  const numbers = value.replace(/\D/g, '');
  
  if (!numbers) return '';
  
  // Converte para centavos
  const amount = parseInt(numbers, 10) / 100;
  
  return formatPrice(amount);
};

/**
 * Aplica máscara de preço mais simples para inputs
 * @param value - Valor sendo digitado
 * @returns Valor formatado com máscara
 */
export const applySimplePriceMask = (value: string): string => {
  // Remove tudo que não é dígito
  const numbers = value.replace(/\D/g, '');
  
  if (!numbers) return '';
  
  // Converte para centavos
  const amount = parseInt(numbers, 10) / 100;
  
  // Formata manualmente para evitar problemas
  const formatted = amount.toFixed(2).replace('.', ',');
  
  // Adiciona pontos para milhares
  const parts = formatted.split(',');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `R$ ${parts.join(',')}`;
};

/**
 * Hook para gerenciar máscara de preço em inputs
 */
export const usePriceMask = () => {
  const handlePriceChange = (value: string, onChange: (value: number) => void) => {
    const formattedValue = applyPriceMask(value);
    const numericValue = parsePrice(formattedValue);
    onChange(numericValue);
    return formattedValue;
  };

  return { handlePriceChange };
};
